import { formatInTimeZone, toDate } from "date-fns-tz";
import { FocusEvent, FunctionComponent, KeyboardEvent, useEffect, useRef, useState } from "react";
import DatePicker, { ReactDatePickerProps } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IconType } from "react-icons";
import { Icons, InputLabel } from "..";
import { InputProps } from "./inputTypes";
import { ValidatedInputGroup } from "./ValidatedInputGroup";

export type DateInputProps = Omit<InputProps<ReactDatePickerProps>, "onChange" | "dateFormat"> & {
    ignoreStartDate?: boolean;
    Icon?: IconType;
    dateFormat?: string;
    displayTimezone?: string;
    onChange: (date: Date | null) => void;
    invalidDateDefault?: Date;
};

const getTimeIfIsOnlyTime = (val?: string) => {
    if (!val) return undefined;

    const timeOnlyRegex = new RegExp("^[\\s]*([\\d]{1,2}:[\\d]{2})[\\s]*(PM|AM|P|A|pm|am|p|a){0,1}[\\s]*$");
    const time = (val ?? "").match(timeOnlyRegex);
    if (time) return time[0];
    return undefined;
};

export const DateInput: FunctionComponent<DateInputProps> = (props) => {
    const { ignoreStartDate, fieldName, validationError, validateModel, value, showTimeInput = true, timeInputLabel = "Time:", dateFormat = "MM/dd/yyyy h:mm aa", className = "w-full", shouldCloseOnSelect = false, startDate: startDateIn, preventOpenOnFocus = true, onChange, invalidDateDefault, Icon = props.Icon ?? Icons.Calendar, ...inputProps } = props;

    const datePicker = useRef<DatePicker>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [textValue, setTextValue] = useState<string>("");
    const [dateValue, setDateValue] = useState<Date | null>(null);
    const isTypingRef = useRef(false);
    const zone = props.displayTimezone ?? Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;

    useEffect(() => {
        if (isTypingRef.current) {
            return;
        }

        if (!value) {
            setTextValue("");
            setDateValue(null);
            return;
        }

        var newDate = toDate(value, { timeZone: zone });

        if (!newDate || newDate.toString() === "Invalid Date") {
            newDate = new Date();
        }

        const newTextValue = formatInTimeZone(newDate, zone, dateFormat);
        setTextValue(newTextValue);
        setDateValue(newDate);
    }, [value, dateFormat, zone]);

    const onChangeRawHandler = (e: FocusEvent<HTMLInputElement>) => {
        isTypingRef.current = true;
        setTextValue(e.target.value);
    };

    const onChangeHandler = (value: Date | [Date, Date] | null) => {
        const isTimePickerOpen = document.getElementsByClassName("react-datepicker__time-container").length > 0;
        const isValidDateValue = value === null || value instanceof Date;
        const isUserTyping = isTypingRef.current;
        const isTimePickerSelection = isValidDateValue && isTimePickerOpen && !isUserTyping;

        if (isTimePickerSelection) {
            const dateToSend = value ? toDate(value, { timeZone: zone }) : null;
            onChange(dateToSend);
        }
    };

    const validateAndSubmitDate = (textInput: string): void => {
        let dv = new Date(textInput);

        if (dv.toString() === "Invalid Date") {
            const defaultDate = invalidDateDefault ?? new Date();
            onChange(defaultDate);
            return;
        }

        if (props.minDate && props.minDate.getTime() > dv.getTime()) {
            dv = props.minDate;
        }
        if (props.maxDate && props.maxDate.getTime() < dv.getTime()) {
            dv = props.maxDate;
        }

        onChange(dv);
    };

    const handleOnBlur = () => {
        setOpen(false);
        isTypingRef.current = false;

        const time = getTimeIfIsOnlyTime(textValue);
        if (time || props.showTimeSelectOnly) {
            const startDateOrNow = startDateIn ?? toDate(new Date(), { timeZone: zone });
            const date = toDate(startDateOrNow!.toDateString() + " " + (time ?? ""), { timeZone: zone });
            onChange(date);
            return;
        }

        if (textValue && textValue.trim() !== "") {
            validateAndSubmitDate(textValue);
            return;
        }

        if (dateValue) {
            onChange(dateValue);
        }
    };

    const handleCalendarClick = () => {
        setOpen(true);

        if (!dateValue) {
            const startDateOrNow = startDateIn ?? toDate(new Date(), { timeZone: zone });
            setDateValue(startDateOrNow);
            onChange(startDateOrNow);
        }
    };

    const handleOnKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            isTypingRef.current = false;

            if (!ignoreStartDate && (!textValue || textValue.trim() === "")) {
                const startDateOrNow = startDateIn ?? toDate(new Date(), { timeZone: zone });
                onChange(startDateOrNow);
                return;
            }

            if (textValue && textValue.trim() !== "") {
                validateAndSubmitDate(textValue);
            }
        }
    };

    const handleClickOutside = () => {
        setOpen(false);
        if (props.showTimeSelectOnly || isTypingRef.current) {
            handleOnBlur();
        }
    };

    return (
        <ValidatedInputGroup fieldName={fieldName} validate={validateModel} validationError={validationError}>
            {({ label, handleBlur }) => (
                <>
                    {props.label && <InputLabel>{props.label}</InputLabel>}
                    <div
                        ref={containerRef}
                        className="relative"
                        onBlur={(e) => {
                            // workaround for react-datepicker: onBlur doesnt fire reliably when typing without opening calendar
                            const focusLeftComponent = containerRef.current && !containerRef.current.contains(e.relatedTarget as Node);
                            const userWasTyping = isTypingRef.current;

                            if (focusLeftComponent && userWasTyping) {
                                handleOnBlur();
                            }
                        }}
                    >
                        <DatePicker
                            popperProps={{ positionFixed: true }}
                            portalId="date-picker-wrapper"
                            ref={datePicker}
                            wrapperClassName={className}
                            selected={dateValue}
                            className={"pr-10 omnisight " + className}
                            popperClassName="z-100"
                            placeholderText={label}
                            onBlur={(e) => {
                                handleBlur();
                                handleOnBlur();
                            }}
                            shouldCloseOnSelect={shouldCloseOnSelect}
                            onChange={onChangeHandler}
                            onKeyDown={handleOnKeyDown}
                            onChangeRaw={onChangeRawHandler}
                            selectsRange={false}
                            onClickOutside={handleClickOutside}
                            showTimeInput={showTimeInput}
                            timeInputLabel={timeInputLabel}
                            dateFormat={dateFormat}
                            onSelect={(e) => {
                                const dateToSend = toDate(e, { timeZone: zone });
                                isTypingRef.current = false; // reset flag so useEffect can sync
                                onChange(dateToSend);
                                setOpen(!shouldCloseOnSelect);
                            }}
                            preventOpenOnFocus={preventOpenOnFocus}
                            open={open}
                            value={textValue}
                            {...inputProps}
                        />
                        <span className="absolute right-3 inline-block bottom-4 cursor-pointer">
                            <Icon onClick={handleCalendarClick} />
                        </span>
                    </div>
                </>
            )}
        </ValidatedInputGroup>
    );
};
